import { z } from 'zod';
import { decimalSchema } from './schemas';

export const actorSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  email: z.string(),
});

export const adjustmentSchema = z.object({
  id: z.string(),
  business_unit_id: z.string(),
  financial_period_id: z.string(),
  financial_category_id: z.string(),
  base_value: decimalSchema,
  adjustment_value: z.string(),
  result_value: z.string(),
  reason: z.string(),
  created_at: z.string(),
  author: actorSchema.nullable().optional(),
});

export const commentSchema = z.object({
  id: z.string(),
  body: z.string(),
  business_unit_id: z.string().nullable(),
  financial_period_id: z.string().nullable(),
  financial_category_id: z.string().nullable(),
  import_id: z.string().nullable(),
  created_at: z.string(),
  author: actorSchema.nullable().optional(),
});

export const auditEntrySchema = z.object({
  id: z.string(),
  created_at: z.string(),
  action: z.string(),
  entity_type: z.string(),
  entity_id: z.string().nullable(),
  payload: z.record(z.unknown()).nullable(),
  ip_address: z.string().nullable(),
  actor: actorSchema.nullable(),
});

export const auditPageSchema = z.object({
  total: z.number(),
  items: z.array(auditEntrySchema),
});

export const valueSourceSchema = z.object({
  import_id: z.string(),
  filename: z.string(),
  revision: z.number(),
  scope_label: z.string().nullable(),
  uploaded_at: z.string(),
  uploaded_by: actorSchema.nullable(),
  confirmed_at: z.string().nullable(),
});

export const valueHistorySchema = z.object({
  version: z.number(),
  amount: decimalSchema,
  source_type: z.string(),
  is_current: z.boolean(),
  created_at: z.string(),
  import_id: z.string().nullable(),
  import_filename: z.string().nullable().optional(),
  import_revision: z.number().nullable().optional(),
});

export const valueDetailSchema = z.object({
  business_unit_id: z.string(),
  period_id: z.string(),
  period_label: z.string(),
  category_id: z.string(),
  category_code: z.string(),
  category_name: z.string(),
  amount: decimalSchema,
  source_type: z.string().nullable(),
  source: valueSourceSchema.nullable(),
  adjustments: z.array(adjustmentSchema),
  comments: z.array(commentSchema),
  history: z.array(valueHistorySchema),
});

export const alertSchema = z.object({
  rule: z.string(),
  severity: z.enum(['INFO', 'WARNING', 'CRITICAL']),
  title: z.string(),
  detail: z.string(),
  business_unit_id: z.string().nullable(),
  metric: z.string().nullable().optional(),
  value: decimalSchema.optional(),
  threshold: decimalSchema.optional(),
});

export const alertsResponseSchema = z.object({
  // Both null before the first import: no period exists, so nothing can be
  // flagged. An empty alert list is a valid answer, not a missing resource.
  period_id: z.string().nullable().optional().default(null),
  period_label: z.string().nullable().optional().default(null),
  alerts: z.array(alertSchema).default([]),
});

export type Actor = z.infer<typeof actorSchema>;
export type Adjustment = z.infer<typeof adjustmentSchema>;
export type CommentItem = z.infer<typeof commentSchema>;
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export type AuditPage = z.infer<typeof auditPageSchema>;
export type ValueDetail = z.infer<typeof valueDetailSchema>;
export type Alert = z.infer<typeof alertSchema>;
export type AlertsResponse = z.infer<typeof alertsResponseSchema>;
